import { memo, type PointerEvent as ReactPointerEvent, type PropsWithChildren } from "react";
import { useReactFlow, type Viewport } from "@xyflow/react";
import { Grip, X } from "lucide-react";
import { componentRegistry } from "../../core/component-registry";
import type { EditorNode, NodeRendererProps } from "../../core/editor-types";
import { useEditorStore } from "../../store/editor-store";
import {
  useCanvasDocumentWindowStore,
  type CanvasDocumentWindowState,
} from "./document-window-store";

interface CanvasDocumentWindowProps extends PropsWithChildren {
  windowState: CanvasDocumentWindowState;
  anchorPosition: { x: number; y: number };
  title: string;
  kindLabel: string;
  Icon: React.ComponentType<{ size?: number | string; className?: string; strokeWidth?: number | string }>;
  minSize: { width: number; height: number };
}

const CanvasDocumentWindow = memo(function CanvasDocumentWindow({
  windowState,
  anchorPosition,
  title,
  kindLabel,
  Icon,
  minSize,
  children,
}: CanvasDocumentWindowProps) {
  const { getZoom } = useReactFlow();
  const close = useCanvasDocumentWindowStore((state) => state.close);
  const raise = useCanvasDocumentWindowStore((state) => state.raise);
  const move = useCanvasDocumentWindowStore((state) => state.move);
  const resize = useCanvasDocumentWindowStore((state) => state.resize);

  const startGesture = (
    event: ReactPointerEvent<HTMLElement>,
    target: "move" | "resize",
  ) => {
    event.preventDefault();
    event.stopPropagation();
    raise(windowState.nodeId);
    const pointerId = event.pointerId;
    const startClient = { x: event.clientX, y: event.clientY };
    const startOffset = { ...windowState.offset };
    const startSize = { ...windowState.size };
    const onPointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const zoom = getZoom();
      const delta = {
        x: (moveEvent.clientX - startClient.x) / zoom,
        y: (moveEvent.clientY - startClient.y) / zoom,
      };
      if (target === "move") {
        move(windowState.nodeId, { x: startOffset.x + delta.x, y: startOffset.y + delta.y });
      } else {
        resize(windowState.nodeId, {
          width: Math.max(minSize.width, startSize.width + delta.x),
          height: Math.max(minSize.height, startSize.height + delta.y),
        });
      }
    };
    const onPointerEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);
  };

  return (
    <section
      className="nodrag nopan nowheel canvas-document-window"
      data-document-node-id={windowState.nodeId}
      aria-label={`${title} 大窗口`}
      style={{
        width: windowState.size.width,
        height: windowState.size.height,
        zIndex: 1400 + windowState.zIndex,
        transform: `translate(${anchorPosition.x + windowState.offset.x}px, ${anchorPosition.y + windowState.offset.y}px)`,
      }}
      onPointerDown={() => raise(windowState.nodeId)}
    >
      <header
        className="canvas-document-window__titlebar"
        onPointerDown={(event) => startGesture(event, "move")}
      >
        <span className="canvas-document-window__kind"><Icon size={14} aria-hidden="true" /> {kindLabel}</span>
        <strong>{title}</strong>
        <button
          type="button"
          className="canvas-document-window__close"
          aria-label={`关闭 ${title} 大窗口`}
          title="关闭"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => close(windowState.nodeId)}
        >
          <X size={15} aria-hidden="true" />
        </button>
      </header>
      <div className="canvas-document-window__content">{children}</div>
      <button
        type="button"
        className="canvas-document-window__resize"
        aria-label={`调整 ${title} 大窗口大小`}
        title="拖动调整窗口大小"
        onPointerDown={(event) => startGesture(event, "resize")}
      >
        <Grip size={13} aria-hidden="true" />
      </button>
    </section>
  );
});

interface CanvasDocumentWindowLayerProps {
  nodes: EditorNode[];
  viewport: Viewport;
}

export function CanvasDocumentWindowLayer({ nodes, viewport }: CanvasDocumentWindowLayerProps) {
  const windows = useCanvasDocumentWindowStore((state) => state.windows);
  const updateNodeData = useEditorStore((state) => state.updateNodeData);

  return (
    <div
      className="canvas-document-window-layer"
      style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})` }}
    >
      {windows.map((windowState) => {
        const node = nodes.find((item) => item.id === windowState.nodeId);
        if (!node) return null;
        const plugin = componentRegistry.get(node.data.kind);
        const definition = plugin?.documentWindow;
        if (!plugin || !definition) return null;
        const Renderer = definition.Renderer as React.ComponentType<NodeRendererProps>;
        const title = definition.getTitle?.(node.data) || node.data.title || plugin.title;
        return (
          <CanvasDocumentWindow
            key={node.id}
            windowState={windowState}
            anchorPosition={node.position}
            title={title}
            kindLabel={definition.kindLabel ?? plugin.title}
            Icon={plugin.icon}
            minSize={definition.minSize ?? { width: 360, height: 260 }}
          >
            <Renderer
              id={node.id}
              data={node.data}
              selected
              update={(patch, historyLabel) => updateNodeData(node.id, patch, historyLabel)}
            />
          </CanvasDocumentWindow>
        );
      })}
    </div>
  );
}
